import type { ContinentSummary, SeverityTier, StreamSnapshot, TimeseriesRow } from '@/types'
import type { Dimension } from '@/types'

export type TimeMode = 'live' | 'historic'
export type TimeWindow = '1h' | '12h' | '24h' | 'all' | 'custom'

export interface TimeQuery {
  mode: TimeMode
  window?: TimeWindow
  startTs?: number
  endTs?: number
}

/**
 * Filter type ids — mirrors backend `FilterTypeSchema`. Includes client-only
 * filter axes (`city`, `os`, `stream_id`, `player_type`) that aren't dimension
 * roll-ups but can still narrow the search.
 */
export type FilterType =
  | 'continent' | 'country' | 'region' | 'city'
  | 'studio' | 'game' | 'brand' | 'stream_id'
  | 'source_url'
  | 'browser' | 'os'
  | 'device_type' | 'platform'
  | 'cdn_edge'
  | 'isp' | 'stream_type' | 'connection_type'
  | 'player_type'

export interface FilterEntry {
  type: FilterType
  value: string
}

/**
 * Filter envelope shared by REST + WS. Same-type entries OR; cross-type AND.
 * `streamId` is broken out so deep-dive narrowing is explicit.
 */
export interface FilterQuery {
  values: FilterEntry[]
  severity: (SeverityTier | null)[]
  streamId: string | null
  userId: string | null
}

/** Parent slice context (e.g. `country/US` to scope a stream listing). */
export interface Slice {
  type: string
  value: string
}

export const DEFAULT_SLICE: Slice = { type: 'global', value: '*' }

/**
 * One unified query envelope shared by REST (`/api/dimension|stream|analysis`)
 * and WS (`subscribe`). The same shape drives every read path.
 */
export interface Query {
  view: 'dimension' | 'stream' | 'analysis' | 'world'
  dimension: string
  slice: Slice
  time: TimeQuery
  filters: FilterQuery
  limit?: number
  offset?: number
  /** World only: client hop grain (continent | country | city). */
  clientGrain?: 'continent' | 'country' | 'city'
  /** Offline grace for hex/stream listings (API default 1h). */
  includeOffline?: '5m' | '1h' | '6h'
}

export const EMPTY_FILTER_QUERY: FilterQuery = {
  values: [],
  severity: [0, 1, 2],
  streamId: null,
  userId: null,
}

export interface WorldAggregateRequest {
  time: TimeQuery
  filters: FilterQuery
  dimension: Dimension
}

export interface WorldPoint {
  id: string
  label?: string
  lat: number
  lon: number
  total: number
  crit: number
  warn: number
  sev: SeverityTier
  // optional extra metrics to display
  mos?: number
  rbf?: number
  std?: number
  totalAll?: number
  active?: number
  nodata?: number

  // Representative stream fields (optional)
  sid?: string
  pop?: string
  con?: string
  country?: string
  geoRegion?: string
  city?: string
  browser?: string
  os?: string
  deviceType?: string
  cdnEdge?: string
  studio?: string
  ip?: string
  sourceUrl?: string
  usersTotal?: number
  
  sevStream?: SeverityTier | null
  ts?: number
  pipeline?: Record<string, unknown>
  metrics?: Record<string, unknown>
  t?: Record<string, unknown>

  // Back-compat: older payloads may still attach metadata under `extra`.
  extra?: Record<string, unknown>
}

export interface WorldAggregateResponse {
  totals?: Record<string, number>
  severities?: { crit: number; warn: number; ok: number; nodata?: number }
  // New: nodes rendered on the world map (dimension-aware, includes geo + table fields).
  nodes?: WorldPoint[]
  // Optional: which SVG country paths belong to each continent code (EU/NA/AS/...)
  // Used by the UI to tint the base map by continent severity.
  continentCountries?: Record<string, string[]>
  // Back-compat: if backend still returns continents, UI can derive points.
  continents?: ContinentSummary[]
  timeseries?: unknown[]
}

export interface StreamsSearchRequest {
  q?: string
  time: TimeQuery
  filters: FilterQuery
}

export interface SearchRequest {
  dimension: Dimension
  time: TimeQuery
  filters: FilterQuery
  limit?: number
  offset?: number
}

export interface SearchResponse {
  critical: number
  warnings: number
  total_streams: number
  total_users: number
  nodes: WorldPoint[]
}

export interface SeriesRequest {
  dimension: Dimension
  value: string
  time: TimeQuery
  filters: FilterQuery
}

export interface SeriesResponse {
  dimension: string
  value: string
  fromMs: number
  toMs: number
  rows: Array<{
    tsMs: number
    usersTotal: number
    streamsTotal: number
    warn: number
    crit: number
    sev: SeverityTier | null
    demuxInRate: number | null
    demuxToDecodeLagMs: number | null
    renderRatio: number | null
    downloadKbps: number | null
    jitterMs: number | null
    packetDelayMs: number | null
    dropFrameMs: number | null
    pFrameDelayMs: number | null
    iFrameDelayMs: number | null
    pipelineLatencyMs: number | null
  }>
}

export interface StreamsSearchResponse {
  // Backend may return either {streams} or {items} depending on implementation.
  streams?: StreamSnapshot[]
  items?: StreamSnapshot[]
  nextCursor?: string
}

export interface StreamsAnalysisRequest {
  time: TimeQuery
  filters: FilterQuery
  sids: string[]
}

export interface StreamAnalysisResult {
  timeseries: TimeseriesRow[]
  summary?: Record<string, unknown>
}

export interface StreamsAnalysisResponse {
  results: Record<string, StreamAnalysisResult>
}

